import React, { useState } from 'react';
import { OutlineButton, PrimaryButton } from '../../../components/Buttons';
import { useNavigate } from 'react-router-dom';
import CreateWorkflow from './CreateWorkflow';
import ProjectSettings from '../../../components/ProjectSettings';
import { 
    Settings, 
    Sparkles, 
    Calendar, 
    Gift, 
    Lightbulb, 
    Megaphone 
} from 'lucide-react';

function Workflow({ project, onProjectUpdate }) {
    const navigate = useNavigate();
    const [showCreateWorkflow, setShowCreateWorkflow] = useState(true); //change to false for production
    const [currentProject, setCurrentProject] = useState(project);

    const allWorkflows = [
        { 
            name: 'Standard', 
            description: 'Default workflow for all projects', 
            leadType: 'standard',
            icon: <Settings className="w-4 h-4" />,
            tag: 'Basic'
        },
        { 
            name: 'Conditional Workflow', 
            description: 'Workflow based on defined conditions', 
            leadType: 'conditional',
            icon: <Sparkles className="w-4 h-4" />,
            tag: 'Advanced'
        },
        { 
            name: 'Birthday Greetings', 
            description: 'Simple birthday wishes for customers', 
            leadType: 'birthday_template',
            icon: <Calendar className="w-4 h-4" />,
            tag: 'Greeting'
        },
        { 
            name: 'Birthday Offers', 
            description: 'Birthday greetings with special offer codes', 
            leadType: 'birthday_offer_template',
            icon: <Gift className="w-4 h-4" />,
            tag: 'Promotional'
        },
        { 
            name: 'Festival Greetings', 
            description: 'Festival celebration wishes', 
            leadType: 'festival_template',
            icon: <Lightbulb className="w-4 h-4" />,
            tag: 'Greeting'
        },
        { 
            name: 'Festival Offers', 
            description: 'Festival greetings with special offers', 
            leadType: 'festival_offer_template',
            icon: <Gift className="w-4 h-4" />,
            tag: 'Promotional'
        },
        // { 
        //     name: 'Ad Campaigns', 
        //     description: 'Workflow for specific ad campaigns', 
        //     leadType: 'adcampaign',
        //     icon: <Megaphone className="w-4 h-4" />,
        //     tag: 'Campaign'
        // },
    ];

    const handleConfigure = (leadType) => {
        navigate(`/workflow/${leadType}`, { state: { project: currentProject, leadType } });
    };

    const handleProjectUpdate = (updatedProject) => {
        setCurrentProject(updatedProject);
        if (onProjectUpdate) {
            onProjectUpdate(updatedProject);
        }
    };

    const handleCloseCreateWorkflow = () => {
        setShowCreateWorkflow(false);
    }

    const getTagColor = (tag) => {
        switch (tag) {
            case 'Basic':
                return 'bg-blue-100 text-blue-700';
            case 'Advanced':
                return 'bg-purple-100 text-purple-700';
            case 'Greeting':
                return 'bg-green-100 text-green-700';
            case 'Promotional':
                return 'bg-yellow-100 text-yellow-700';
            case 'Campaign':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="h-full overflow-auto bg-gray-50">
            <div className="p-4">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-base font-semibold text-gray-900">All Workflows</h3>
                            <span className="text-sm text-gray-500">({allWorkflows.length} workflows)</span>
                        </div>
                        
                        {/* Project Settings */}
                        <ProjectSettings project={currentProject} onProjectUpdate={handleProjectUpdate} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {allWorkflows.map((workflow) => (
                            <div 
                                key={workflow.leadType} 
                                className="group relative bg-white rounded-lg border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col"
                            >
                                {/* Tag */}
                                <div className="absolute top-2 right-2 z-10">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTagColor(workflow.tag)}`}>
                                        {workflow.tag}
                                    </span>
                                </div>

                                {/* Card Content */}
                                <div className="p-4 pt-6 flex-1 flex flex-col">
                                    <div className="flex items-start space-x-3 mb-3 flex-1">
                                        <div className="flex-shrink-0 p-2 bg-flamingo-100 rounded-lg text-flamingo-600 group-hover:bg-flamingo-200 group-hover:text-flamingo-700 transition-all duration-200">
                                            {workflow.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-200 mb-1">
                                                {workflow.name}
                                            </h4>
                                            <p className="text-xs text-gray-600 leading-relaxed">
                                                {workflow.description}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Button - Always at bottom */}
                                    <div className="mt-auto pt-3">
                                        <PrimaryButton 
                                            onClick={() => handleConfigure(workflow.leadType)}
                                            className="w-full px-3 py-2 rounded-md hover:bg-flamingo-800 transition-all duration-200 text-xs"
                                        >
                                            Configure
                                        </PrimaryButton>
                                    </div>
                                </div>

                                {/* Hover Effect Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-gray-900/0 to-gray-900/0 group-hover:from-gray-900/5 group-hover:to-gray-900/5 transition-all duration-200 pointer-events-none rounded-lg"></div>
                            </div>
                        ))}
                    </div>
                </div>
            
                {/* {showCreateWorkflow && (
                    <div className="mt-4">
                        <CreateWorkflow onClose={handleCloseCreateWorkflow} />
                    </div>
                )} */}
            </div>
        </div>
    );
}

export default Workflow;